import React, { useCallback, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faSpinner,
    faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import Modal from '../../common/modals/Modal'
import Button from '../../common/form/inputs/Button'
import LlmPromptInput from '../../common/llm/LlmPromptInput'

type Props = {
    onClose: () => void
    onGenerate: (jobDescription: string) => void
    savedJobDescription?: string | null
    hasRoleContext?: boolean
    isGenerating?: boolean
}

export default function GenerateWithAiModal({
    onClose,
    onGenerate,
    savedJobDescription,
    hasRoleContext = false,
    isGenerating = false,
}: Props): React.JSX.Element {
    const [jobDescription, setJobDescription] = useState('')

    const handleUseSavedDescription = useCallback(() => {
        if (!savedJobDescription) return
        setJobDescription(savedJobDescription)
    }, [savedJobDescription])

    const handleGenerate = useCallback(() => {
        const trimmedDescription = jobDescription.trim()
        if (!trimmedDescription) return
        onGenerate(trimmedDescription)
    }, [jobDescription, onGenerate])

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault()
                handleGenerate()
            }
        },
        [handleGenerate],
    )

    const trimmedDescription = jobDescription.trim()
    const canUseSavedDescription =
        hasRoleContext && Boolean(savedJobDescription?.trim())
    const isGeneratingRequest = Boolean(isGenerating)
    const submitButtonContent = isGeneratingRequest ? (
        <>
            <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="mr-2 text-gray-600"
            />
            Populating filters...
        </>
    ) : (
        <>
            <FontAwesomeIcon icon={faWandMagicSparkles} className="mr-2" />
            Populate Filters
        </>
    )

    return (
        <Modal
            title="Auto Populate Filters"
            onClose={onClose}
            size="large"
            allowUserToClose={!isGeneratingRequest}
        >
            <div className="space-y-4">
                {canUseSavedDescription ? (
                    <Button
                        type="button"
                        style="secondary"
                        onClick={handleUseSavedDescription}
                        enabled={Boolean(savedJobDescription?.trim())}
                        classes=""
                    >
                        Use Saved Job Description
                    </Button>
                ) : null}

                <LlmPromptInput
                    textareaId="filters-ai-job-description"
                    value={jobDescription}
                    onChange={setJobDescription}
                    onSubmit={handleGenerate}
                    onKeyDown={handleKeyDown}
                    placeholder="Paste a job description or describe the role..."
                    disableSubmit={
                        isGeneratingRequest || trimmedDescription.length === 0
                    }
                    submitButtonContent={submitButtonContent}
                />
            </div>
        </Modal>
    )
}
